/**
 * Transcription Jobs Admin — upload audio/video, watch jobs run,
 * download captions. Mounted at /admin/transcription.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Upload, Loader2, FileText, CheckCircle2, XCircle, Clock, Download,
  RotateCcw, Trash2, Languages, Mic, Pencil, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const fetchJson = async (path, opts = {}) => {
  const r = await fetch(`${API}/api${path}`, {
    ...opts,
    headers: { 'Authorization': `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(typeof e.detail === 'string' ? e.detail : (e.detail?.message || `HTTP ${r.status}`));
  }
  return r.json();
};

const STATUS = {
  queued:     { color: 'bg-slate-100 text-slate-700',   icon: Clock },
  processing: { color: 'bg-blue-100 text-blue-700',     icon: Loader2 },
  completed:  { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  edited:     { color: 'bg-indigo-100 text-indigo-700', icon: Pencil },
  failed:     { color: 'bg-red-100 text-red-700',       icon: XCircle },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS[status] || { color: 'bg-slate-100 text-slate-700', icon: Clock };
  const Icon = cfg.icon;
  return <Badge className={`${cfg.color} gap-1`}><Icon className={`h-3 w-3 ${status === 'processing' ? 'animate-spin' : ''}`} />{status}</Badge>;
};

const TranscriptionJobsAdmin = () => {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState('');
  const [engine, setEngine] = useState('whisper');
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');

  // Poll every 4s while there's an active job so progress is visible.
  const { data, isLoading } = useQuery({
    queryKey: ['transcription-jobs'],
    queryFn: () => fetchJson('/transcription/jobs?limit=100'),
    refetchInterval: (q) => {
      const jobs = q.state.data?.jobs || [];
      return jobs.some(j => ['queued', 'processing'].includes(j.status)) ? 4000 : false;
    },
  });
  const { data: enginesData } = useQuery({
    queryKey: ['transcription-engines'],
    queryFn: () => fetchJson('/transcription/engines'),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('file', file);
      if (title) fd.append('title', title);
      if (language) fd.append('language', language);
      fd.append('engine', engine);
      return fetchJson('/transcription/jobs', { method: 'POST', body: fd });
    },
    onSuccess: () => {
      toast.success('Queued — transcription will run in the background.');
      setFile(null); setTitle(''); setLanguage('');
      qc.invalidateQueries({ queryKey: ['transcription-jobs'] });
    },
    onError: e => toast.error(e.message),
  });

  const retryMutation = useMutation({
    mutationFn: (id) => fetchJson(`/transcription/jobs/${id}/retry`, { method: 'POST' }),
    onSuccess: () => { toast.success('Re-queued'); qc.invalidateQueries({ queryKey: ['transcription-jobs'] }); },
    onError: e => toast.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (id) => fetchJson(`/transcription/jobs/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries({ queryKey: ['transcription-jobs'] }); },
    onError: e => toast.error(e.message),
  });
  const editMutation = useMutation({
    mutationFn: ({ id, transcript }) => fetchJson(`/transcription/jobs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcript }),
    }),
    onSuccess: () => { toast.success('Transcript saved'); setEditing(null); qc.invalidateQueries({ queryKey: ['transcription-jobs'] }); },
    onError: e => toast.error(e.message),
  });

  const downloadExport = async (id, fmt) => {
    const r = await fetch(`${API}/api/transcription/jobs/${id}/export.${fmt}`, {
      headers: { 'Authorization': `Bearer ${getToken()}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `transcript-${id}.${fmt}`; a.click();
    URL.revokeObjectURL(url);
  };

  const jobs = data?.jobs || [];
  const engines = enginesData?.engines || [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="transcription-admin">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><Mic className="h-6 w-6" /> Transcription Jobs</h1>
        <p className="text-sm text-slate-500">Upload class recordings, sermons, conference sessions or any audio/video; get back searchable text and captions.</p>
      </div>

      {/* Upload card */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" /> New job</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_160px_160px_120px] gap-3 items-end">
            <div>
              <Label className="text-xs">Audio / video file</Label>
              <Input type="file" accept="audio/*,video/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="transcription-file-input" />
            </div>
            <div>
              <Label className="text-xs">Title (optional)</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={file?.name || 'Untitled'} data-testid="transcription-title-input" />
            </div>
            <div>
              <Label className="text-xs">Language (optional)</Label>
              <Input value={language} onChange={e => setLanguage(e.target.value)} placeholder="auto-detect" data-testid="transcription-lang-input" />
            </div>
            <div>
              <Label className="text-xs">Engine</Label>
              <Select value={engine} onValueChange={setEngine}>
                <SelectTrigger data-testid="transcription-engine-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {engines.map(e => <SelectItem key={e.name} value={e.name}>{e.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => uploadMutation.mutate()} disabled={!file || uploadMutation.isPending} data-testid="transcription-upload-btn">
              {uploadMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
              Upload
            </Button>
          </div>
          {file && <p className="text-xs text-slate-500 mt-2">Ready: {file.name} ({(file.size/1024/1024).toFixed(1)} MB)</p>}
        </CardContent>
      </Card>

      {/* Jobs table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between gap-2">
            <span>Jobs</span>
            <Badge variant="outline">{jobs.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : jobs.length === 0 ? (
            <p className="text-center text-slate-400 py-10 text-sm">No transcription jobs yet — upload a recording above.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Engine · Lang</TableHead>
                  <TableHead className="hidden lg:table-cell">Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Size · Segments</TableHead>
                  <TableHead className="text-right w-full max-w-[260px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map(j => (
                  <TableRow key={j.id} data-testid={`transcription-job-row-${j.id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{j.title}</p>
                      <p className="text-[10px] text-slate-400">{j.file_name}</p>
                    </TableCell>
                    <TableCell className="text-xs hidden md:table-cell">
                      <Badge variant="outline" className="text-[10px]">{j.engine}</Badge>
                      {' '}
                      {j.detected_language && <Badge variant="outline" className="text-[10px]"><Languages className="h-2.5 w-2.5 mr-0.5" />{j.detected_language}</Badge>}
                    </TableCell>
                    <TableCell className="text-xs hidden lg:table-cell">{j.created_at ? new Date(j.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell>
                      <StatusBadge status={j.status} />
                      {j.error && <p className="text-[10px] text-red-600 mt-0.5 max-w-[200px] truncate" title={j.error}>{j.error}</p>}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {(j.file_size_bytes/1024/1024).toFixed(1)} MB
                      <br />
                      <span className="text-slate-400">{j.segment_count || 0} segs</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end flex-wrap">
                        {j.status === 'completed' || j.status === 'edited' ? (
                          <>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(j.id); setEditText(j.transcript || ''); }} data-testid={`transcription-edit-${j.id}`}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadExport(j.id, 'srt')} data-testid={`transcription-export-srt-${j.id}`}>
                              <Download className="h-3 w-3 mr-0.5" />SRT
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadExport(j.id, 'vtt')}>
                              <Download className="h-3 w-3 mr-0.5" />VTT
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadExport(j.id, 'txt')}>
                              <FileText className="h-3 w-3" />
                            </Button>
                          </>
                        ) : null}
                        {j.status === 'failed' && (
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => retryMutation.mutate(j.id)} data-testid={`transcription-retry-${j.id}`}>
                            <RotateCcw className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => { if (window.confirm('Delete this job and its source file?')) deleteMutation.mutate(j.id); }} data-testid={`transcription-delete-${j.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit transcript dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Edit transcript</DialogTitle></DialogHeader>
          <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={18} data-testid="transcription-edit-textarea" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editMutation.mutate({ id: editing, transcript: editText })} disabled={editMutation.isPending} data-testid="transcription-edit-save">
              <Save className="h-4 w-4 mr-1" />Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TranscriptionJobsAdmin;
